import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { downloadTicketPdf } from '../utils/ticketDownloadUtils';
import { useEffect, useState } from 'react';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import Dialog from '@mui/material/Dialog';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import PdfIcon from '@mui/icons-material/PictureAsPdf';
import FormHelperText from '@mui/material/FormHelperText';
import api from '../api';
import printApi from '../printApi';
import LanguageSelector from './LanguageSelector';
import { TIMEOUT_LOCALHOST, TIMEOUT_NONE } from '../constants';

export default function TicketPdfSelectorDialog({ orderId, eventId, onClose }) {
  const { t } = useTranslation();

  const [selectedType, setSelectedType] = useState('ONLINE');
  const [language, setLanguage] = useState('ro');
  const [isLoading, setIsLoading] = useState(false);
  const [preparedDownload, setPreparedDownload] = useState(null);
  const [isLoadingPrint, setIsLoadingPrint] = useState(false);
  const [isLoadingPrinterStatus, setIsLoadingPrinterStatus] = useState(false);
  const [isPrinterConnected, setIsPrinterConnected] = useState(false);
  const [isServerReachable, setIsServerReachable] = useState(true);

  useEffect(() => {
    setPreparedDownload(null);
  }, [orderId, eventId, selectedType, language]);

  useEffect(() => {
    if (!orderId || selectedType !== 'PRINT') return;
    setIsLoadingPrinterStatus(true);

    printApi
      .get('/health', { timeout: TIMEOUT_LOCALHOST })
      .then(async (res) => {
        setIsServerReachable(true);
        res.data.ok ? setIsPrinterConnected(true) : setIsPrinterConnected(false);
        setIsLoadingPrinterStatus(false);
      })
      .catch((err) => {
        setIsLoadingPrinterStatus(false);
        setIsServerReachable(false);
      });
  }, [orderId, t, selectedType]);

  const onCloseInternal = (event, reason) => {
    if (reason !== 'backdropClick') {
      setPreparedDownload(null);
      setSelectedType('ONLINE');
      onClose();
    }
  };

  const onConfirmInternal = () => {
    setIsLoading(true);
    downloadTicketPdf(
      () =>
        api
          .get(
            `/orders/${orderId}/tickets?type=${selectedType}${`&language=${language}`}${
              eventId ? `&eventId=${eventId}` : ''
            }`,
            {
              timeout: TIMEOUT_NONE,
              responseType: 'arraybuffer',
            }
          )
          .then((res) => res.data),
      `tickets_order_${orderId}.pdf`
    )
      .then((download) => {
        setIsLoading(false);
        if (download) {
          setPreparedDownload(download);
        } else {
          onCloseInternal();
        }
      })
      .catch(() => {
        setIsLoading(false);
        toast.error(t('An error occured please try again!'), { position: toast.POSITION.BOTTOM_RIGHT });
      });
  };

  const onConfirmPrint = () => {
    setIsLoadingPrint(true);

    api
      .get(
        `/orders/${orderId}/ticketsZip?type=${selectedType}${`&language=${language}`}${
          eventId ? `&eventId=${eventId}` : ''
        }`,
        {
          timeout: TIMEOUT_NONE,
          responseType: 'blob',
        }
      )
      .then(async (res) => {
        const zipBlob = res.data;

        const formData = new FormData();
        formData.append('zip_file', zipBlob, 'tickets.zip');
        const printRes = await printApi.post('/print', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        if (!printRes.status || printRes.status !== 200) {
          throw new Error('Failed to print');
        }

        setIsLoadingPrint(false);
        onCloseInternal();
      })
      .catch(() => {
        setIsLoadingPrint(false);
        toast.error(t('An error occured please try again!'), { position: toast.POSITION.BOTTOM_RIGHT });
      });
  };
  return (
    <Dialog fullWidth maxWidth="xs" open={!!orderId} onClose={onCloseInternal}>
      <DialogTitle gutterBottom>
        <Box display="flex" alignItems="center">
          <PdfIcon sx={{ marginRight: 1 }} />
          {t(`Download`) + ` #${orderId} ` + t(`ticket(s)`)}
        </Box>
        <IconButton
          aria-label="close"
          onClick={onCloseInternal}
          sx={{
            position: 'absolute',
            right: 8,
            top: 10,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Box display="flex" alignItems="center" gap={2}>
          <TextField
            value={selectedType}
            onChange={(event) => setSelectedType(event.target.value)}
            fullWidth
            select
            label={t('Type')}
          >
            <MenuItem selected value={'OFFLINE'}>
              {t('Offline')}
            </MenuItem>
            <MenuItem value={'ONLINE'}>{t('OnlineTicket')}</MenuItem>
            <MenuItem value={'PRINT'}>{t('ThermalPrinter')}</MenuItem>
          </TextField>
          <LanguageSelector language={language} setLanguage={setLanguage} />
        </Box>
        {(!isPrinterConnected || !isServerReachable) && selectedType === 'PRINT' && !isLoadingPrinterStatus && (
          <FormHelperText error sx={{ mt: 1, ml: 1 }}>
            {!isServerReachable ? t('ServerNotConnected') : !isPrinterConnected ? t('PrinterNotConnected') : ''}
          </FormHelperText>
        )}
        {isLoadingPrinterStatus && <FormHelperText sx={{ mt: 1, ml: 1 }}>{t('PrinterIsConnecting')}</FormHelperText>}
      </DialogContent>
      <DialogActions>
        {selectedType === 'PRINT' ? (
          <Button
            disabled={isLoadingPrint || !isPrinterConnected || !isServerReachable}
            onClick={onConfirmPrint}
            variant="contained"
          >
            {t('Print')}
            {isLoadingPrint && <CircularProgress size={20} sx={{ ml: 1, color: 'white' }} />}
          </Button>
        ) : preparedDownload ? (
          <Button
            component="a"
            href={preparedDownload.url}
            download={preparedDownload.filename}
            target="_blank"
            rel="noopener"
            onClick={onCloseInternal}
            variant="contained"
          >
            {t('Download')}
          </Button>
        ) : (
          <Button disabled={isLoading} onClick={onConfirmInternal} variant="contained">
            {t('Download')}
            {isLoading && <CircularProgress size={20} sx={{ ml: 1, color: 'white' }} />}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
